import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Send, Loader2, MessageSquare,
  Flame, Volume2, VolumeX, Mic, MicOff, User, Users, Sparkles,
  Settings, Music, Globe, MessageCircle, HelpCircle, Mail, Crown, Lock, ImageIcon,
  Star, ThumbsUp, ThumbsDown, Download, X, Flag, AlertTriangle, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dialogues required for each level
const LEVEL_DIALOGUES = {
  2: 5,
  3: 25,
  4: 60,
  5: 120
};

const LEVEL_NAMES = {
  fr: {
    1: 'Romantique',
    2: 'Sensuel',
    3: 'Intime',
    4: 'Passionné',
    5: 'MyErotika'
  },
  en: {
    1: 'Romantic',
    2: 'Sensual',
    3: 'Intimate',
    4: 'Passionate',
    5: 'MyErotika'
  },
  es: {
    1: 'Romántico',
    2: 'Sensual',
    3: 'Íntimo',
    4: 'Apasionado',
    5: 'MyErotika'
  },
  de: {
    1: 'Romantisch',
    2: 'Sinnlich',
    3: 'Intim',
    4: 'Leidenschaftlich',
    5: 'MyErotika'
  },
  pt: {
    1: 'Romântico',
    2: 'Sensual',
    3: 'Íntimo',
    4: 'Apaixonado',
    5: 'MyErotika'
  }
};

export default function ChatPage() {
  const navigate = useNavigate();
  const { gender, characterId, voiceId, level } = useParams();
  const { 
    user, token, logout, 
    isCharacterUnlocked, isLevelUnlockedForCharacter, 
    getCharacterDialogues, getCharacterLevels,
    updateCharacterProgress 
  } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [character, setCharacter] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(false);  // Default to text mode
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newLevel, setNewLevel] = useState(null);
  const [localDialogues, setLocalDialogues] = useState(0);
  const [localUnlockedLevels, setLocalUnlockedLevels] = useState([1]);
  const [sessionMessageCount, setSessionMessageCount] = useState(0);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [purchasingLevel, setPurchasingLevel] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [listenDuration, setListenDuration] = useState(0);
  const listenTimerRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(() => localStorage.getItem('velvet_music_muted') !== 'true');
  const [responseLength, setResponseLength] = useState(() => localStorage.getItem('velvet_response_length') || 'medium');
  const [photosUnlocked, setPhotosUnlocked] = useState(false);
  const [purchasingPhotos, setPurchasingPhotos] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewStep, setReviewStep] = useState(1);
  const [reviewLiked, setReviewLiked] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewDone, setReviewDone] = useState(() => localStorage.getItem('velvet_review_done') === 'true');
  const [reviewDismissed, setReviewDismissed] = useState(false);
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  const [notifGranted, setNotifGranted] = useState(() => localStorage.getItem('erotika_notif_granted') === 'true');
  const [notifDenied, setNotifDenied] = useState(() => localStorage.getItem('erotika_notif_denied') === 'true');
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // {type: 'message'|'photo'|'conversation', index, content, photoUrl}
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState(false);

  const REPORT_REASONS = {
    fr: [
      { value: 'non_consenting_sexual', label: 'Contenu sexuel non consenti' },
      { value: 'violent_degrading', label: 'Propos violents ou dégradants' },
      { value: 'minor_reference', label: 'Référence à des mineurs' },
      { value: 'discriminatory', label: 'Contenu discriminatoire' },
      { value: 'other', label: 'Autre' }
    ],
    en: [
      { value: 'non_consenting_sexual', label: 'Non-consenting sexual content' },
      { value: 'violent_degrading', label: 'Violent or degrading language' },
      { value: 'minor_reference', label: 'Reference to minors' },
      { value: 'discriminatory', label: 'Discriminatory content' },
      { value: 'other', label: 'Other' }
    ]
  };

  const handleReport = (target) => {
    setReportTarget(target);
    setReportReason('');
    setReportDescription('');
    setReportDone(false);
    setReportOpen(true);
  };

  const submitReport = async () => {
    if (!reportReason) return;
    if (reportReason === 'other' && !reportDescription.trim()) return;
    setReportSubmitting(true);
    try {
      await axios.post(`${API}/reports`, {
        reason: reportReason,
        description: reportDescription || null,
        character_id: characterId,
        conversation_id: conversationId,
        message_index: reportTarget?.index ?? null,
        message_content: reportTarget?.content ?? null,
        photo_url: reportTarget?.photoUrl ?? null
      }, { headers: { Authorization: `Bearer ${token}` } });
      setReportDone(true);
    } catch (err) {
      console.error('Report error:', err);
    } finally {
      setReportSubmitting(false);
    }
  };
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const previousUnlockedLevelsRef = useRef([1]);
  const recognitionRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Fix mobile keyboard covering input area
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.style.height = `${vv.height}px`;
      }
    };

    handleResize();
    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);

    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, []);

  // Scroll input into view when focused (mobile keyboard)
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    };

    input.addEventListener('focus', handleFocus);
    return () => input.removeEventListener('focus', handleFocus);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    // Only auto-scroll when new messages are added, not on initial load
    if (messages.length > prevMessageCountRef.current && prevMessageCountRef.current > 0) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  // Check if user just returned from a purchase — trigger review
  useEffect(() => {
    if (reviewDone || reviewDismissed) return;
    const purchaseFlag = localStorage.getItem('erotika_review_after_purchase');
    if (purchaseFlag) {
      localStorage.removeItem('erotika_review_after_purchase');
      setTimeout(() => setShowReview(true), 3000);
    }
  }, []);

  // Initialize Speech Recognition
  const speechTextRef = useRef('');
  const isListeningRef = useRef(false);
  
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', pt: 'pt-PT' }[language] || 'en-US';
      
      recognition.onresult = (event) => {
        // Only process NEW final results using resultIndex
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          speechTextRef.current += transcript;
        }
        setInput(speechTextRef.current);
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          isListeningRef.current = false;
          setIsListening(false);
          toast.error(language === 'fr' 
            ? 'Microphone non autorisé. Veuillez autoriser l\'accès.' 
            : 'Microphone not allowed. Please grant access.');
        }
      };
      
      recognition.onend = () => {
        isListeningRef.current = false;
        setIsListening(false);
        if (listenTimerRef.current) { clearInterval(listenTimerRef.current); listenTimerRef.current = null; }
      };
      
      recognitionRef.current = recognition;
    }
    
    return () => {
      isListeningRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (listenTimerRef.current) { clearInterval(listenTimerRef.current); }
    };
  }, [language]);

  // Toggle speech recognition
  const toggleListening = () => {
    if (!speechSupported) {
      toast.error(language === 'fr' 
        ? 'La reconnaissance vocale n\'est pas supportée par votre navigateur' 
        : 'Speech recognition is not supported by your browser');
      return;
    }
    
    if (isListening) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      if (listenTimerRef.current) { clearInterval(listenTimerRef.current); listenTimerRef.current = null; }
      setListenDuration(0);
    } else {
      // Stop any playing voice first
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        setIsPlayingVoice(false);
      }
      
      try {
        speechTextRef.current = input;
        isListeningRef.current = true;
        recognitionRef.current?.start();
        setIsListening(true);
        setListenDuration(0);
        listenTimerRef.current = setInterval(() => {
          setListenDuration(prev => prev + 1);
        }, 1000);
      } catch (error) {
        console.error('Speech recognition start error:', error);
      }
    }
  };

  // Initialize local state from auth context
  useEffect(() => {
    const dialogues = getCharacterDialogues(characterId);
    const levels = getCharacterLevels(characterId);
    setLocalDialogues(dialogues);
    setLocalUnlockedLevels(levels);
    previousUnlockedLevelsRef.current = levels;
  }, [characterId, getCharacterDialogues, getCharacterLevels]);

  useEffect(() => {
    fetchCharacter();
  }, []);

  // Check access
  useEffect(() => {
    if (!isLevelUnlockedForCharacter(characterId, parseInt(level))) {
      navigate(`/select-level/${gender}/${characterId}/${voiceId}`);
    }
    if (!isCharacterUnlocked(characterId)) {
      navigate(`/select-character/${gender}`);
    }
  }, [level, characterId, isLevelUnlockedForCharacter, isCharacterUnlocked, navigate, gender, voiceId]);

  // Load existing conversation for this character on mount
  useEffect(() => {
    const loadExistingConversation = async () => {
      try {
        const response = await axios.get(`${API}/chat/character/${characterId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.conversation) {
          setConversationId(response.data.conversation.id);
          const msgs = response.data.conversation.messages || [];
          setMessages(msgs);
          prevMessageCountRef.current = msgs.length;
          // Check if any photo is locked
          const hasLockedPhoto = msgs.some(m => m.photo_locked);
          setPhotosUnlocked(!hasLockedPhoto && msgs.some(m => m.image_url));
          // Scroll to bottom after initial load
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant' }), 100);
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    loadExistingConversation();
  }, [characterId, token]);

  const fetchCharacter = async () => {
    try {
      const response = await axios.get(`${API}/characters/${gender}?language=${language}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const char = response.data.find(c => c.id === characterId);
      setCharacter(char);
    } catch (error) {
      console.error('Error fetching character:', error);
    }
  };

  // Generate and play voice for AI response
  const playVoice = async (text) => {
    if (!voiceEnabled || !text) return;
    
    // Stop any currently playing audio
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
    }
    
    try {
      setIsPlayingVoice(true);
      
      const response = await axios.post(`${API}/tts/generate`, {
        text: text,
        character_id: characterId,
        language: language
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.audio_url) {
        const audio = new Audio(response.data.audio_url);
        voiceAudioRef.current = audio;
        
        audio.onended = () => {
          setIsPlayingVoice(false);
          voiceAudioRef.current = null;
        };
        
        audio.onerror = () => {
          setIsPlayingVoice(false);
          voiceAudioRef.current = null;
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error('Voice generation error:', error);
      setIsPlayingVoice(false);
    }
  };

  // Stop voice playback
  const stopVoice = () => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
    }
    setIsPlayingVoice(false);
  };

  // Toggle voice and stop if currently playing
  const toggleVoice = () => {
    if (voiceEnabled && isPlayingVoice) {
      stopVoice();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    speechTextRef.current = '';
    setLoading(true);

    // Stop voice recording if active
    if (isListeningRef.current) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      if (listenTimerRef.current) { clearInterval(listenTimerRef.current); listenTimerRef.current = null; }
      setListenDuration(0);
    }

    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: userMessage.content,
        conversation_id: conversationId,
        gender,
        character_id: characterId,
        voice_id: voiceId,
        eroticism_level: parseInt(level),
        language: language,
        response_length: responseLength
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConversationId(response.data.conversation_id);
      
      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      
      // Check if backend returned a new photo message (always last in the array)
      const backendMessages = response.data.messages || [];
      const lastBackendMsg = backendMessages[backendMessages.length - 1];
      const newPhotoMsg = (lastBackendMsg && lastBackendMsg.image_url) ? lastBackendMsg : null;
      
      if (newPhotoMsg) {
        setMessages(prev => [...prev, aiMessage, newPhotoMsg]);
      } else {
        setMessages(prev => [...prev, aiMessage]);
      }
      
      // Trigger review after every 20th user message (if not already reviewed or dismissed)
      if (!reviewDone && !reviewDismissed) {
        const totalUserMsgs = messages.filter(m => m.role === 'user').length + 1; // +1 for the message just sent
        if (totalUserMsgs > 0 && totalUserMsgs % 20 === 0) {
          setTimeout(() => setShowReview(true), 2000);
        }
      }
      
      // Trigger notification permission prompt at 15th msg, then every 25 msgs
      if (!notifGranted && !notifDenied && 'Notification' in window && Notification.permission !== 'granted') {
        const totalUserMsgs = messages.filter(m => m.role === 'user').length + 1;
        if (totalUserMsgs === 15 || (totalUserMsgs > 15 && (totalUserMsgs - 15) % 25 === 0)) {
          setTimeout(() => setShowNotifPrompt(true), 3000);
        }
      }
      
      // Play voice for AI response if enabled
      if (voiceEnabled) {
        playVoice(response.data.response);
      }
      
      // Increment session message count for upgrade prompt
      const newSessionCount = sessionMessageCount + 1;
      setSessionMessageCount(newSessionCount);
      
      // Update progress for this character
      if (response.data.character_progress) {
        const progress = response.data.character_progress;
        setLocalDialogues(progress.dialogues);
        setLocalUnlockedLevels(progress.unlocked_levels);
        
        // Update auth context
        updateCharacterProgress(characterId, progress);
        
        // Check for new level unlock
        if (response.data.new_level_unlocked) {
          setNewLevel(response.data.new_level_unlocked);
          setShowLevelUp(true);
        } else {
          // Check if user reached threshold for a paid level (4 or 5)
          const eligible = progress.eligible_levels || [];
          if (eligible.length > 0) {
            // Threshold reached but payment needed
            setShowUpgradePrompt(true);
          } else {
            // Show upgrade prompt every 15 messages if there are locked levels
            const maxUnlockedLevel = Math.max(...(progress.unlocked_levels || [1]));
            if (newSessionCount % 15 === 0 && maxUnlockedLevel < 5) {
              setShowUpgradePrompt(true);
            }
          }
        }
      }
    } catch (error) {
      console.error('Send message error:', error);
      if (error.response?.status === 403) {
        toast.error(language === 'fr' ? 'Accès non autorisé' : 'Access not authorized');
        navigate(`/select-level/${gender}/${characterId}/${voiceId}`);
      } else {
        toast.error(error.response?.data?.detail || t('error'));
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  // Cleanup voice audio on unmount
  useEffect(() => {
    return () => {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
      }
    };
  }, []);

  const characterName = character?.name || characterId;

  // Handle quick level purchase from prompt
  const handleQuickLevelPurchase = async () => {
    const maxUnlockedLevel = Math.max(...(localUnlockedLevels || [1]));
    const nextLevel = maxUnlockedLevel + 1;
    
    if (nextLevel > 5) return;
    
    setPurchasingLevel(true);
    try {
      const returnPath = `/chat/${gender}/${characterId}/${nextLevel}`;
      localStorage.setItem('erotika_payment_return', returnPath);

      const response = await axios.post(`${API}/purchase/checkout`, {
        item_type: 'level',
        item_id: `${characterId}:${nextLevel}`,
        origin_url: window.location.origin,
        return_to: returnPath
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasingLevel(false);
      setShowUpgradePrompt(false);
    }
  };

  // Get the next level name for the prompt
  const getNextLevelName = () => {
    const maxUnlockedLevel = Math.max(...(localUnlockedLevels || [1]));
    const nextLevel = maxUnlockedLevel + 1;
    return nextLevel <= 5 ? (LEVEL_NAMES[language] || LEVEL_NAMES.fr)[nextLevel] : null;
  };

  // Calculate next level progress for this character
  const getNextLevelInfo = () => {
    const unlockedLevels = localUnlockedLevels || [1];
    const currentMaxUnlocked = Math.max(...unlockedLevels);
    const nextLevel = currentMaxUnlocked + 1;
    
    if (nextLevel > 5) {
      return { nextLevel: null, required: 0, progress: 100 };
    }
    
    const required = LEVEL_DIALOGUES[nextLevel] || 0;
    const progress = required > 0 ? Math.min(100, (localDialogues / required) * 100) : 100;
    
    return { nextLevel, required, progress };
  };

  const nextLevelInfo = getNextLevelInfo();

  const handleUnlockPhotos = async () => {
    setPurchasingPhotos(true);
    try {
      const returnPath = `/chat/${gender}/${characterId}/${level}`;
      localStorage.setItem('erotika_payment_return', returnPath);

      const response = await axios.post(`${API}/purchase/checkout`, {
        item_type: 'photos',
        item_id: characterId,
        origin_url: window.location.origin,
        return_to: returnPath
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setPurchasingPhotos(false);
    }
  };

  const handleSubmitReview = async () => {
    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        liked: reviewLiked,
        rating: reviewLiked ? reviewRating : null,
        comment: reviewComment || null,
        character_id: characterId
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem('velvet_review_done', 'true');
      setReviewDone(true);
      setShowReview(false);
      toast.success(language === 'fr' ? 'Merci pour votre avis !' : language === 'es' ? '¡Gracias por tu opinión!' : language === 'de' ? 'Danke für Ihre Bewertung!' : language === 'pt' ? 'Obrigado pela sua opinião!' : 'Thank you for your review!');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Push notification subscription handler
  const handleEnableNotifications = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        const vapidRes = await axios.get(`${API}/notifications/vapid-key`);
        const vapidKey = vapidRes.data.public_key;

        const urlBase64ToUint8Array = (base64String) => {
          const padding = '='.repeat((4 - base64String.length % 4) % 4);
          const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(base64);
          return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
        };

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        await axios.post(`${API}/notifications/subscribe`, {
          subscription: subscription.toJSON(),
          character_id: characterId,
          gender: gender,
          level: parseInt(level),
        }, { headers: { Authorization: `Bearer ${token}` } });

        localStorage.setItem('erotika_notif_granted', 'true');
        localStorage.removeItem('erotika_notif_denied');
        setNotifGranted(true);
        setShowNotifPrompt(false);
        toast.success(language === 'fr' ? 'Notifications activées !' : language === 'es' ? '¡Notificaciones activadas!' : language === 'de' ? 'Benachrichtigungen aktiviert!' : language === 'pt' ? 'Notificações ativadas!' : 'Notifications enabled!');
      } else {
        localStorage.setItem('erotika_notif_denied', 'true');
        setNotifDenied(true);
        setShowNotifPrompt(false);
      }
    } catch (err) {
      console.error('Notification subscription error:', err);
      setShowNotifPrompt(false);
    }
  };

  return (
    <>
    <div ref={chatContainerRef} className="bg-velvet-bg flex flex-col overflow-hidden" style={{ height: 'calc(100dvh - env(safe-area-inset-top, 0px))' }}>
      {/* Level Up Modal */}
      {showLevelUp && newLevel && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="glass rounded-2xl p-8 max-w-md text-center animate-slideUp">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-main to-fuchsia-main flex items-center justify-center">
              <Sparkles className="text-white" size={40} />
            </div>
            <h2 className="font-heading text-3xl gradient-text mb-4">
              {language === 'fr' ? 'Félicitations !' : 'Congratulations!'}
            </h2>
            <p className="text-velvet_text-primary text-lg mb-2">
              {language === 'fr' 
                ? 'Vous avez atteint un nouveau niveau de complicité !'
                : 'You have reached a new level of intimacy!'
              }
            </p>
            <div className="flex items-center justify-center gap-2 mb-6">
              {[...Array(newLevel)].map((_, i) => (
                <Flame key={i} size={24} className="text-purple-light fill-current" />
              ))}
            </div>
            <p className="text-velvet_text-accent text-xl font-heading mb-6">
              {(LEVEL_NAMES[language] || LEVEL_NAMES.fr)[newLevel]}
            </p>
            <Button
              onClick={() => {
                setShowLevelUp(false);
                // Navigate to the new level's chat
                navigate(`/chat/${gender}/${characterId}/${newLevel}`);
              }}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full px-8 py-3"
            >
              {language === 'fr' ? 'Passer au niveau suivant' : 'Go to next level'}
            </Button>
            <Button
              onClick={() => setShowLevelUp(false)}
              variant="ghost"
              className="text-velvet_text-secondary hover:text-velvet_text-primary rounded-full px-6 py-2 mt-2"
            >
              {language === 'fr' ? 'Rester ici' : 'Stay here'}
            </Button>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal - Shows every 15 messages or when paid level threshold reached */}
      {showUpgradePrompt && getNextLevelName() && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="glass rounded-2xl p-8 max-w-md text-center animate-slideUp border border-purple-main/30">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-main/30 to-fuchsia-main/30 flex items-center justify-center">
              <Flame className="text-purple-light" size={32} />
            </div>
            <p className="text-velvet_text-primary text-lg mb-4 leading-relaxed">
              {language === 'fr' 
                ? `Vous avez atteint le seuil pour passer au niveau "${getNextLevelName()}" avec ${characterName} !`
                : `You've reached the threshold for the "${getNextLevelName()}" level with ${characterName}!`
              }
            </p>
            <p className="text-velvet_text-accent text-xl font-heading mb-6">
              {language === 'fr' 
                ? <span>Débloquez maintenant pour <span className="line-through opacity-60">1,99 €</span> <span className="font-bold">0,99 €</span> (paiement unique)</span>
                : <span>Unlock now for <span className="line-through opacity-60">€1.99</span> <span className="font-bold">€0.99</span> (one-time payment)</span>
              }
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleQuickLevelPurchase}
                disabled={purchasingLevel}
                className="bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full px-6 py-3"
                data-testid="upgrade-prompt-buy"
              >
                {purchasingLevel ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <Sparkles size={16} className="mr-2" />
                    {language === 'fr' ? (<><span className="line-through opacity-60 mr-1">1,99 €</span> Débloquer 0,99 €</>) : (<><span className="line-through opacity-60 mr-1">€1.99</span> Unlock €0.99</>)}
                  </>
                )}
              </Button>
            </div>
            <button
              onClick={() => setShowUpgradePrompt(false)}
              className="text-velvet_text-secondary/60 hover:text-velvet_text-secondary text-xs mt-4 transition-colors"
              data-testid="upgrade-prompt-dismiss"
            >
              {({fr: 'Continuer la discussion', en: 'Continue chatting', es: 'Seguir conversando', de: 'Weiter chatten', pt: 'Continuar a conversa'})[language] || 'Continue chatting'}
            </button>
            <button
              onClick={() => navigate('/select-gender')}
              className="block mx-auto text-velvet_text-secondary/40 hover:text-velvet_text-secondary text-xs mt-2 transition-colors"
              data-testid="upgrade-prompt-new-character"
            >
              {({fr: 'Choisir un autre personnage', en: 'Choose another character', es: 'Elegir otro personaje', de: 'Anderen Charakter wählen', pt: 'Escolher outro personagem'})[language] || 'Choose another character'}
            </button>
          </div>
        </div>
      )}

      {/* Progress Bar Header */}
      <div className="glass border-b border-white/5 px-4 pt-2 pb-5" style={{ overflow: 'visible' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <MessageSquare size={14} className="text-purple-light" />
              {nextLevelInfo.nextLevel ? (
                <span className="text-velvet_text-secondary text-xs">
                  <span className="text-velvet_text-accent font-bold">{localDialogues}/{nextLevelInfo.required}</span>
                  {(() => {
                    const levelName = (LEVEL_NAMES[language] || LEVEL_NAMES.fr)[nextLevelInfo.nextLevel];
                    const beforeText = { fr: 'avant', en: 'before', es: 'antes de', de: 'vor', pt: 'antes de' };
                    return ` ${beforeText[language] || beforeText.en} "${levelName}"`;
                  })()}
                </span>
              ) : (
                <span className="text-velvet_text-accent text-xs font-medium">
                  {language === 'fr' ? 'Tous les niveaux débloqués !' : language === 'es' ? '¡Todos los niveles desbloqueados!' : language === 'de' ? 'Alle Stufen freigeschaltet!' : language === 'pt' ? 'Todos os níveis desbloqueados!' : 'All levels unlocked!'}
                </span>
              )}
            </div>
          </div>
          <div className="relative" style={{ overflow: 'visible' }}>
            <Progress value={nextLevelInfo.progress} className="h-2.5 rounded-full" />
            <button
              onClick={() => navigate(`/select-level/${gender}/${characterId}`)}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main shadow-lg shadow-purple-main/40 hover:scale-110 text-white transition-all font-bold border-2 border-velvet-bg leading-none"
              style={{ fontSize: '18px', paddingBottom: '2px' }}
              data-testid="currency-level-button"
              title={language === 'fr' ? 'Débloquer des niveaux' : 'Unlock levels'}
            >
              {['fr', 'de', 'es', 'pt'].includes(language) ? '€' : '$'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Main Chat Area */}
        <main className="flex-1 flex flex-col min-h-0">
          {/* Chat Header */}
          <header className="glass border-b border-white/5 px-4 py-3 flex items-center gap-4">
            <button 
              onClick={() => navigate(`/select-level/${gender}/${characterId}/${voiceId}`)}
              className="text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
              data-testid="back-to-select"
            >
              <ArrowLeft size={20} />
            </button>

            <div className="flex items-center gap-3 flex-1">
              {character?.image && (
                <img 
                  src={character.image}
                  alt={characterName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              )}
              <div>
                <h2 className="text-velvet_text-primary font-medium flex items-center gap-2">
                  {characterName}
                  {gender === 'couple' ? <Users size={16} className="text-purple-light" /> : <User size={16} className="text-purple-light" />}
                </h2>
                <div className="flex items-center gap-1">
                  {[...Array(parseInt(level))].map((_, i) => (
                    <Flame key={i} size={12} className="text-purple-light fill-current" />
                  ))}
                  <span className="text-velvet_text-secondary text-xs ml-1">
                    {t(`level_${level}`)}
                  </span>
                </div>
              </div>
            </div>

            {/* Settings gear button */}
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-velvet_text-secondary hover:text-velvet_text-accent transition-all"
              data-testid="settings-button"
            >
              <Settings size={20} />
            </button>
          </header>

          {/* Settings Sheet */}
          <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
            <SheetContent side="right" className="bg-velvet-bg border-white/10 w-[320px] sm:w-[380px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-velvet_text-primary font-heading text-xl">
                  {language === 'fr' ? 'Paramètres' : language === 'es' ? 'Ajustes' : language === 'de' ? 'Einstellungen' : language === 'pt' ? 'Definições' : 'Settings'}
                </SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-5">
                {/* Subscription / Purchase */}
                <div className="glass rounded-xl p-4 space-y-3" data-testid="settings-purchase-section">
                  <Button
                    onClick={async () => {
                      setSettingsOpen(false);
                      try {
                        const returnPath = `/chat/${gender}/${characterId}/${level}`;
                        localStorage.setItem('erotika_payment_return', returnPath);

                        const response = await axios.post(`${API}/purchase/checkout`, {
                          item_type: 'subscription',
                          item_id: 'all_unlimited',
                          origin_url: window.location.origin,
                          return_to: returnPath
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        window.location.href = response.data.checkout_url;
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Erreur');
                      }
                    }}
                    className="w-full bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-full text-sm h-auto py-2 flex flex-col items-center"
                    data-testid="settings-subscribe-button"
                  >
                    <span className="font-semibold">{language === 'fr' ? 'Tout illimité' : 'Unlimited access'}</span>
                    <span className="text-xs mt-0.5"><span className="line-through opacity-70 mr-1">{language === 'fr' ? '9,99 €/mois' : '€9.99/mo'}</span> {language === 'fr' ? '4,99 €/mois' : '€4.99/mo'}</span>
                  </Button>
                  
                  {getNextLevelName() && (
                    <Button
                      onClick={() => { setSettingsOpen(false); handleQuickLevelPurchase(); }}
                      variant="outline"
                      className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-full text-sm h-auto py-2 flex flex-col items-center"
                      data-testid="settings-next-level-button"
                    >
                      <span className="font-semibold">{language === 'fr' ? `Niveau "${getNextLevelName()}"` : `Level "${getNextLevelName()}"`}</span>
                      <span className="text-xs mt-0.5"><span className="line-through opacity-60 mr-1">{language === 'fr' ? '1,99 €' : '€1.99'}</span> {language === 'fr' ? '0,99 €' : '€0.99'}</span>
                    </Button>
                  )}
                  
                  {!photosUnlocked && (
                    <Button
                      onClick={() => { setSettingsOpen(false); handleUnlockPhotos(); }}
                      variant="outline"
                      className="w-full border-white/20 text-velvet_text-primary hover:bg-white/10 rounded-full text-sm h-auto py-2 flex flex-col items-center"
                      data-testid="settings-unlock-photos-button"
                    >
                      <span className="font-semibold flex items-center gap-1">
                        <ImageIcon size={14} />
                        {language === 'fr' ? `Toutes les photos de ${characterName}` : `All of ${characterName}'s photos`}
                      </span>
                      <span className="text-xs mt-0.5 text-velvet_text-secondary">
                        {language === 'fr' ? 'Accès illimité aux photos actuelles et futures' : 'Unlimited access to current and future photos'}
                      </span>
                      <span className="text-xs mt-0.5"><span className="line-through opacity-60 mr-1">{language === 'fr' ? '1,99 €' : '€1.99'}</span> {language === 'fr' ? '0,99 €' : '€0.99'}</span>
                    </Button>
                  )}
                </div>

                {/* Music toggle */}
                <div className="glass rounded-xl p-4 flex items-center justify-between" data-testid="settings-music-toggle">
                  <div className="flex items-center gap-3">
                    <Music size={18} className="text-purple-light" />
                    <span className="text-velvet_text-primary text-sm">
                      {{ fr: 'Musique', en: 'Music', es: 'Música', de: 'Musik', pt: 'Música' }[language] || 'Music'}
                    </span>
                  </div>
                  <Switch
                    checked={musicEnabled}
                    onCheckedChange={(checked) => {
                      setMusicEnabled(checked);
                      localStorage.setItem('velvet_music_muted', !checked ? 'true' : 'false');
                      window.dispatchEvent(new CustomEvent('musicToggle', { detail: { muted: !checked } }));
                    }}
                    className="data-[state=checked]:bg-purple-main data-[state=unchecked]:bg-white/30"
                    data-testid="music-switch"
                  />
                </div>

                {/* TTS toggle */}
                <div className="glass rounded-xl p-4 flex items-center justify-between" data-testid="settings-tts-toggle">
                  <div className="flex items-center gap-3">
                    {voiceEnabled ? <Volume2 size={18} className="text-purple-light" /> : <VolumeX size={18} className="text-purple-light" />}
                    <span className="text-velvet_text-primary text-sm">
                      {{ fr: 'Synthèse vocale', en: 'Text-to-speech', es: 'Síntesis de voz', de: 'Sprachsynthese', pt: 'Síntese de voz' }[language] || 'Text-to-speech'}
                    </span>
                  </div>
                  <Switch
                    checked={voiceEnabled}
                    onCheckedChange={(checked) => {
                      if (!checked && isPlayingVoice) stopVoice();
                      setVoiceEnabled(checked);
                    }}
                    className="data-[state=checked]:bg-purple-main data-[state=unchecked]:bg-white/30"
                    data-testid="tts-switch"
                  />
                </div>

                {/* Language */}
                <div className="glass rounded-xl p-4" data-testid="settings-language">
                  <div className="flex items-center gap-3 mb-3">
                    <Globe size={18} className="text-purple-light" />
                    <span className="text-velvet_text-primary text-sm">
                      {language === 'fr' ? 'Langue' : language === 'es' ? 'Idioma' : language === 'de' ? 'Sprache' : language === 'pt' ? 'Idioma' : 'Language'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { code: 'fr', label: 'FR' },
                      { code: 'en', label: 'EN' },
                      { code: 'es', label: 'ES' },
                      { code: 'de', label: 'DE' },
                      { code: 'pt', label: 'PT' }
                    ].map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          language === lang.code 
                            ? 'bg-purple-main text-white' 
                            : 'bg-white/10 text-velvet_text-secondary hover:bg-white/20'
                        }`}
                        data-testid={`lang-${lang.code}-button`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Response length */}
                <div className="glass rounded-xl p-4" data-testid="settings-response-length">
                  <div className="flex items-center gap-3 mb-3">
                    <MessageCircle size={18} className="text-purple-light" />
                    <span className="text-velvet_text-primary text-sm">
                      {{ fr: 'Longueur des réponses', en: 'Response length', es: 'Longitud de respuesta', de: 'Antwortlänge', pt: 'Tamanho da resposta' }[language] || 'Response length'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { value: 'short', fr: 'Court', en: 'Short', es: 'Corto', de: 'Kurz', pt: 'Curto' },
                      { value: 'medium', fr: 'Moyen', en: 'Medium', es: 'Medio', de: 'Mittel', pt: 'Médio' },
                      { value: 'long', fr: 'Long', en: 'Long', es: 'Largo', de: 'Lang', pt: 'Longo' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setResponseLength(opt.value);
                          localStorage.setItem('velvet_response_length', opt.value);
                        }}
                        className={`flex-1 py-2 rounded-full text-xs font-medium transition-all ${
                          responseLength === opt.value 
                            ? 'bg-purple-main text-white' 
                            : 'bg-white/10 text-velvet_text-secondary hover:bg-white/20'
                        }`}
                        data-testid={`length-${opt.value}-button`}
                      >
                        {opt[language] || opt.en}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10" />

                {/* FAQ */}
                <button
                  onClick={() => { setSettingsOpen(false); navigate('/faq'); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-velvet_text-accent transition-colors"
                  data-testid="settings-faq-button"
                >
                  <HelpCircle size={18} className="text-purple-light" />
                  <span className="text-sm">{{ fr: 'Questions fréquentes (FAQ)', en: 'FAQ', es: 'Preguntas frecuentes (FAQ)', de: 'Häufige Fragen (FAQ)', pt: 'Perguntas frequentes (FAQ)' }[language] || 'FAQ'}</span>
                </button>

                {/* Contact */}
                <button
                  onClick={() => { setSettingsOpen(false); navigate('/contact'); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-primary hover:text-velvet_text-accent transition-colors"
                  data-testid="settings-contact-button"
                >
                  <Mail size={18} className="text-purple-light" />
                  <span className="text-sm">{{ fr: 'Nous contacter', en: 'Contact us', es: 'Contáctanos', de: 'Kontaktiere uns', pt: 'Contacte-nos' }[language] || 'Contact us'}</span>
                </button>

                {/* Report conversation */}
                <button
                  onClick={() => { setSettingsOpen(false); handleReport({ type: 'conversation', index: null, content: null, photoUrl: null }); }}
                  className="glass rounded-xl p-4 w-full flex items-center gap-3 text-velvet_text-secondary hover:text-red-400 transition-colors"
                  data-testid="settings-report-button"
                >
                  <AlertTriangle size={18} className="text-red-400/60" />
                  <span className="text-sm">{{ fr: 'Signaler un contenu', en: 'Report content', es: 'Reportar contenido', de: 'Inhalt melden', pt: 'Denunciar conteúdo' }[language] || 'Report content'}</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4" data-testid="messages-area">
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-20">
                  {character?.image && (
                    <div className="w-24 h-24 rounded-full mx-auto mb-6 overflow-hidden">
                      <img 
                        src={character.image}
                        alt={characterName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <h3 className="font-heading text-2xl text-velvet_text-primary mb-2">{characterName}</h3>
                  <p className="text-velvet_text-accent text-sm mb-2">{character?.personality}</p>
                  <p className="text-velvet_text-secondary">
                    {language === 'fr' 
                      ? `Commencez une conversation avec ${characterName}...`
                      : `Start a conversation with ${characterName}...`
                    }
                  </p>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slideUp`}
                >
                  <div
                    className={`
                      max-w-[80%] p-4 rounded-2xl
                      ${msg.role === 'user' 
                        ? 'chat-bubble-user text-purple-100' 
                        : 'chat-bubble-ai text-zinc-200'
                      }
                    `}
                    data-testid={`message-${index}`}
                  >
                    {msg.image_url && (
                      <div className="mb-3 rounded-xl overflow-hidden relative">
                        {/\.(mp4|webm|mov)(\?|$)/i.test(msg.image_url) ? (
                          <video
                            src={msg.image_url}
                            controls
                            playsInline
                            preload="metadata"
                            className={`w-full max-w-[280px] h-auto rounded-xl ${msg.photo_locked ? 'blur-xl pointer-events-none' : ''}`}
                            data-testid={`video-${index}`}
                          />
                        ) : (
                        <img 
                          src={msg.image_url} 
                          alt="" 
                          className={`w-full max-w-[280px] h-auto rounded-xl ${msg.photo_locked ? 'blur-xl' : 'cursor-pointer active:scale-95 transition-transform'}`}
                          loading="lazy"
                          onClick={() => { if (!msg.photo_locked) setFullscreenPhoto(msg.image_url); }}
                          data-testid={`photo-${index}`}
                        />
                        )}
                        {msg.photo_locked && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 rounded-xl">
                            <Lock size={28} className="text-white mb-2" />
                            <p className="text-white text-xs font-medium mb-2 text-center px-4">
                              {language === 'fr' ? `Débloquez toutes les photos de ${characterName}` : `Unlock all of ${characterName}'s photos`}
                            </p>
                            <button
                              onClick={handleUnlockPhotos}
                              disabled={purchasingPhotos}
                              className="bg-gradient-to-r from-purple-main to-fuchsia-main text-white text-xs font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                              data-testid="unlock-photos-button"
                            >
                              {purchasingPhotos 
                                ? <Loader2 className="animate-spin" size={14} />
                                : (<>
                                    <ImageIcon size={12} className="inline mr-1" />
                                    <span className="line-through opacity-60 mr-1">1,99 €</span> 0,99 €
                                  </>)
                              }
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs opacity-50">
                        {new Date(msg.timestamp).toLocaleTimeString({ fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', pt: 'pt-PT' }[language] || 'en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                      {msg.role === 'assistant' && (
                        <button
                          onClick={() => handleReport({
                            type: msg.image_url ? 'photo' : 'message',
                            index,
                            content: msg.content,
                            photoUrl: msg.image_url || null
                          })}
                          className="text-white/20 hover:text-red-400 transition-colors p-1"
                          data-testid={`report-msg-${index}`}
                          title={language === 'fr' ? 'Signaler' : 'Report'}
                        >
                          <Flag size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start animate-slideUp">
                  <div className="chat-bubble-ai px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-purple-light animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-2 h-2 rounded-full bg-purple-light animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-2 h-2 rounded-full bg-purple-light animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="glass border-t border-white/5 p-4 shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex gap-3">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isListening 
                  ? (language === 'fr' ? 'Parlez maintenant...' : 'Speak now...') 
                  : t('type_message')
                }
                className={`flex-1 bg-velvet-paper border-white/10 text-velvet_text-primary h-12 rounded-xl ${
                  isListening ? 'border-red-500/60' : ''
                }`}
                disabled={loading}
                data-testid="chat-input"
              />
              {/* Microphone button */}
              {speechSupported && (
                <div className="relative flex items-center">
                  {isListening && (
                    <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap">
                      <span className="text-red-400 text-xs font-mono tabular-nums">
                        {Math.floor(listenDuration / 60).toString().padStart(2, '0')}:{(listenDuration % 60).toString().padStart(2, '0')}
                      </span>
                      <div className="flex items-center gap-[3px] h-6">
                        {[1,2,3,4,5].map(i => (
                          <div 
                            key={i}
                            className="w-[3px] bg-red-400 rounded-full"
                            style={{
                              animation: `voiceWave 0.8s ease-in-out ${i * 0.1}s infinite alternate`,
                              height: '8px',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={toggleListening}
                    className={`rounded-xl h-12 w-12 relative overflow-hidden ${
                      isListening 
                        ? 'bg-red-500 hover:bg-red-600' 
                        : 'bg-velvet-paper hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid="mic-button"
                    title={language === 'fr' ? 'Dicter le message' : 'Dictate message'}
                  >
                    {isListening && (
                      <span className="absolute inset-0 rounded-xl border-2 border-red-400 animate-ping opacity-30" />
                    )}
                    {isListening ? (
                      <MicOff size={20} className="text-white relative z-10" />
                    ) : (
                      <Mic size={20} className="text-velvet_text-secondary" />
                    )}
                  </Button>
                </div>
              )}
              <Button
                type="submit"
                disabled={!input.trim() || loading}
                className="bg-purple-main hover:bg-purple-dark text-white rounded-xl h-12 w-12"
                data-testid="send-button"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
              </Button>
            </form>
          </div>
        </main>
      </div>

      {/* Review Modal */}
      {showReview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="glass rounded-2xl p-6 max-w-sm w-full animate-slideUp border border-purple-main/30">
            
            {/* Step 1: Do you like the app? */}
            {reviewStep === 1 && (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-main/30 to-fuchsia-main/30 flex items-center justify-center">
                  <Star className="text-purple-light" size={28} />
                </div>
                <h3 className="text-velvet_text-primary font-heading text-xl mb-2">
                  {language === 'fr' ? 'Aimez-vous MyErotika ?' : language === 'es' ? '¿Te gusta MyErotika?' : language === 'de' ? 'Gefällt Ihnen MyErotika?' : language === 'pt' ? 'Gosta do MyErotika?' : 'Do you like MyErotika?'}
                </h3>
                <p className="text-velvet_text-secondary text-sm mb-6">
                  {language === 'fr' ? 'Votre avis nous aide à nous améliorer' : language === 'es' ? 'Tu opinión nos ayuda a mejorar' : language === 'de' ? 'Ihre Meinung hilft uns, besser zu werden' : language === 'pt' ? 'A sua opinião ajuda-nos a melhorar' : 'Your feedback helps us improve'}
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => { setReviewLiked(false); setReviewStep(2); }}
                    className="flex flex-col items-center gap-2 px-6 py-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
                    data-testid="review-dislike"
                  >
                    <ThumbsDown size={28} className="text-velvet_text-secondary" />
                    <span className="text-velvet_text-secondary text-sm">
                      {language === 'fr' ? 'Non' : language === 'es' ? 'No' : language === 'de' ? 'Nein' : language === 'pt' ? 'Não' : 'No'}
                    </span>
                  </button>
                  <button
                    onClick={() => { setReviewLiked(true); setReviewStep(2); }}
                    className="flex flex-col items-center gap-2 px-6 py-4 rounded-xl bg-purple-main/20 hover:bg-purple-main/30 transition-colors border border-purple-main/30"
                    data-testid="review-like"
                  >
                    <ThumbsUp size={28} className="text-purple-light" />
                    <span className="text-purple-light text-sm">
                      {language === 'fr' ? 'Oui' : language === 'es' ? 'Sí' : language === 'de' ? 'Ja' : language === 'pt' ? 'Sim' : 'Yes'}
                    </span>
                  </button>
                </div>
                <button
                  onClick={() => { setShowReview(false); setReviewDismissed(true); }}
                  className="mt-4 text-velvet_text-secondary text-xs hover:text-velvet_text-primary transition-colors"
                  data-testid="review-dismiss"
                >
                  {language === 'fr' ? 'Plus tard' : language === 'es' ? 'Más tarde' : language === 'de' ? 'Später' : language === 'pt' ? 'Mais tarde' : 'Later'}
                </button>
              </div>
            )}

            {/* Step 2a: User doesn't like - free comment */}
            {reviewStep === 2 && reviewLiked === false && (
              <div>
                <h3 className="text-velvet_text-primary font-heading text-lg mb-2">
                  {language === 'fr' ? 'Que pouvons-nous améliorer ?' : language === 'es' ? '¿Qué podemos mejorar?' : language === 'de' ? 'Was können wir verbessern?' : language === 'pt' ? 'O que podemos melhorar?' : 'What can we improve?'}
                </h3>
                <p className="text-velvet_text-secondary text-sm mb-4">
                  {language === 'fr' ? 'Dites-nous ce qui vous plairait davantage' : language === 'es' ? 'Cuéntanos qué te gustaría más' : language === 'de' ? 'Sagen Sie uns, was Ihnen besser gefallen würde' : language === 'pt' ? 'Diga-nos o que gostaria mais' : 'Tell us what you would enjoy more'}
                </p>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="w-full bg-velvet-paper border border-white/10 text-velvet_text-primary rounded-xl p-3 min-h-[120px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-main/50 mb-4 text-sm"
                  placeholder={language === 'fr' ? 'Votre commentaire...' : language === 'es' ? 'Tu comentario...' : language === 'de' ? 'Ihr Kommentar...' : language === 'pt' ? 'O seu comentário...' : 'Your comment...'}
                  data-testid="review-comment-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setReviewStep(1); setReviewComment(''); }}
                    variant="ghost"
                    className="flex-1 text-velvet_text-secondary rounded-full"
                  >
                    {t('back')}
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    disabled={reviewSubmitting}
                    className="flex-1 bg-gradient-to-r from-purple-main to-fuchsia-main text-white rounded-full"
                    data-testid="review-submit"
                  >
                    {reviewSubmitting ? <Loader2 className="animate-spin" size={16} /> : (language === 'fr' ? 'Envoyer' : t('send'))}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2b: User likes - star rating + optional comment */}
            {reviewStep === 2 && reviewLiked === true && (
              <div>
                <h3 className="text-velvet_text-primary font-heading text-lg mb-2 text-center">
                  {language === 'fr' ? 'Super ! Notez-nous' : language === 'es' ? '¡Genial! Califícanos' : language === 'de' ? 'Toll! Bewerten Sie uns' : language === 'pt' ? 'Ótimo! Avalie-nos' : 'Great! Rate us'}
                </h3>
                <div className="flex justify-center gap-2 my-5">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="transition-transform hover:scale-110"
                      data-testid={`review-star-${star}`}
                    >
                      <Star
                        size={36}
                        className={`transition-colors ${
                          star <= reviewRating 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-white/20'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="w-full bg-velvet-paper border border-white/10 text-velvet_text-primary rounded-xl p-3 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-main/50 mb-4 text-sm"
                  placeholder={language === 'fr' ? 'Un commentaire ? (optionnel)' : language === 'es' ? '¿Un comentario? (opcional)' : language === 'de' ? 'Ein Kommentar? (optional)' : language === 'pt' ? 'Um comentário? (opcional)' : 'A comment? (optional)'}
                  data-testid="review-comment-input"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setReviewStep(1); setReviewRating(0); setReviewComment(''); }}
                    variant="ghost"
                    className="flex-1 text-velvet_text-secondary rounded-full"
                  >
                    {t('back')}
                  </Button>
                  <Button
                    onClick={handleSubmitReview}
                    disabled={reviewSubmitting || reviewRating === 0}
                    className="flex-1 bg-gradient-to-r from-purple-main to-fuchsia-main text-white rounded-full disabled:opacity-50"
                    data-testid="review-submit"
                  >
                    {reviewSubmitting ? <Loader2 className="animate-spin" size={16} /> : (language === 'fr' ? 'Envoyer' : t('send'))}
                  </Button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>

    {/* Report Dialog */}
    <AlertDialog open={reportOpen} onOpenChange={setReportOpen}>

    {/* Notification Permission Prompt */}
    {showNotifPrompt && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
        <div className="glass rounded-3xl p-6 max-w-sm w-full border border-white/10 shadow-2xl text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main flex items-center justify-center">
            <Bell size={28} className="text-white" />
          </div>
          <h3 className="text-velvet_text-primary font-heading text-lg mb-2">
            {language === 'fr' ? 'Ne manquez aucun message' : language === 'es' ? 'No te pierdas ningún mensaje' : language === 'de' ? 'Verpasse keine Nachricht' : language === 'pt' ? 'Não perca nenhuma mensagem' : "Don't miss any message"}
          </h3>
          <p className="text-velvet_text-secondary text-sm mb-5">
            {language === 'fr' ? `Activez les notifications pour recevoir les messages de vos personnages préférés, même quand vous n'êtes pas sur l'appli.`
            : language === 'es' ? 'Activa las notificaciones para recibir mensajes de tus personajes favoritos, incluso cuando no estés en la app.'
            : language === 'de' ? 'Aktiviere Benachrichtigungen, um Nachrichten von deinen Lieblingscharakteren zu erhalten, auch wenn du nicht in der App bist.'
            : language === 'pt' ? 'Ative as notificações para receber mensagens dos seus personagens favoritos, mesmo quando não estiver no app.'
            : 'Enable notifications to receive messages from your favorite characters, even when you\'re not on the app.'}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full border-white/20 text-velvet_text-secondary hover:text-white"
              onClick={() => { setShowNotifPrompt(false); }}
              data-testid="notif-dismiss"
            >
              {language === 'fr' ? 'Plus tard' : language === 'es' ? 'Más tarde' : language === 'de' ? 'Später' : language === 'pt' ? 'Mais tarde' : 'Later'}
            </Button>
            <Button
              className="flex-1 rounded-full bg-gradient-to-r from-purple-main to-fuchsia-main text-white hover:opacity-90"
              onClick={handleEnableNotifications}
              data-testid="notif-accept"
            >
              {language === 'fr' ? 'Activer' : language === 'es' ? 'Activar' : language === 'de' ? 'Aktivieren' : language === 'pt' ? 'Ativar' : 'Enable'}
            </Button>
          </div>
        </div>
      </div>
    )}
      <AlertDialogContent className="bg-velvet-bg border-white/10 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-velvet_text-primary flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-400" />
            {language === 'fr' ? 'Signaler un contenu' : 'Report content'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-velvet_text-secondary">
            {reportDone 
              ? (language === 'fr' 
                ? 'Merci pour votre signalement. Notre équipe va examiner ce contenu.'
                : 'Thank you for your report. Our team will review this content.')
              : (language === 'fr'
                ? 'Sélectionnez la raison de votre signalement :'
                : 'Select the reason for your report:')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {reportDone ? (
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setReportOpen(false)}
              className="bg-purple-main hover:bg-purple-dark text-white rounded-full"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        ) : (
          <>
            <div className="space-y-2 my-2">
              {(REPORT_REASONS[language] || REPORT_REASONS.fr).map(r => (
                <button
                  key={r.value}
                  onClick={() => setReportReason(r.value)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-all ${
                    reportReason === r.value
                      ? 'bg-red-500/20 border border-red-400/40 text-red-300'
                      : 'bg-white/5 border border-white/10 text-velvet_text-secondary hover:bg-white/10'
                  }`}
                  data-testid={`report-reason-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Description field - always shown, required for "other" */}
            <textarea
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder={language === 'fr' ? 'Décrivez le problème (optionnel)...' : 'Describe the issue (optional)...'}
              className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-velvet_text-primary placeholder:text-velvet_text-secondary/50 resize-none h-20 focus:outline-none focus:border-purple-main/50"
              data-testid="report-description"
            />
            {reportReason === 'other' && !reportDescription.trim() && (
              <p className="text-red-400 text-xs">{language === 'fr' ? 'Description obligatoire pour "Autre"' : 'Description required for "Other"'}</p>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10 text-velvet_text-secondary hover:bg-white/10 rounded-full">
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </AlertDialogCancel>
              <button
                onClick={submitReport}
                disabled={!reportReason || reportSubmitting || (reportReason === 'other' && !reportDescription.trim())}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full px-6 py-2 text-sm font-medium transition-colors"
                data-testid="report-submit"
              >
                {reportSubmitting 
                  ? <Loader2 className="animate-spin" size={16} />
                  : (language === 'fr' ? 'Envoyer' : 'Submit')}
              </button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>

    {/* Photo Fullscreen Lightbox */}
    {fullscreenPhoto && (
      <div 
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        onClick={() => setFullscreenPhoto(null)}
        data-testid="photo-lightbox"
      >
        <button
          onClick={() => setFullscreenPhoto(null)}
          className="absolute top-4 right-4 text-white/70 hover:text-white bg-white/10 rounded-full p-2 backdrop-blur-sm transition-colors z-10"
          data-testid="lightbox-close"
        >
          <X size={24} />
        </button>
        <a
          href={fullscreenPhoto}
          download={`erotika_${characterName}_${Date.now()}.jpg`}
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm transition-colors z-10"
          data-testid="lightbox-download"
        >
          <Download size={24} />
        </a>
        <img 
          src={fullscreenPhoto} 
          alt="" 
          className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
}
